import {
  Box,
  Flex,
  Icon,
  SimpleGrid,
  useColorModeValue,
  Heading,
  Text,
  Button,
} from '@chakra-ui/react';
import Sidebar from 'components/sidebar/Sidebar';
import React, { useEffect, useState, useCallback } from 'react';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import {
  MdDevices,
  MdMemory,
  MdStorage,
  MdSdStorage,
  MdBackup,
} from 'react-icons/md';
import ComplexTable from '../default/components/ComplexTable';
import axios from 'axios'; // Keep axios for health fetch
import apiUrls from 'api/apiUrls';
import { useNavigate } from 'react-router-dom';
import BreadCrumb from 'components/breadCrumb/BreadCrumb';
import { sortDevices } from 'views/hooks/sortDevices';
import axiosInstance from 'api/axiosInstance';

const DeviceHealth = ({ inEditDevice, onViewHealthDetails }) => {
  // const TOTALS_BY_TYPE = {
  //   1: {
  //     // MINI
  //     ram: 163840,
  //     disk: 1048576,
  //     cpu: 100,
  //     backup: 1073741824,
  //   },
  //   2: {
  //     // GRIDX
  //     ram: 505413632,
  //     disk: 3650722201,
  //     cpu: 100,
  //     backup: 1073741824,
  //   },
  //   3: {
  //     // MINI
  //     ram: 163840,
  //     disk: 1048576,
  //     cpu: 100,
  //     backup: 1073741824,
  //   },
  //   // Add more types as needed
  // };
  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'Device Health', path: '/modbus-configs' },
  ];

  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [totalsConfig, setTotalsConfig] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);

  const [devices, setDevices] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [totalDeviceCount, setTotalDeviceCount] = useState(0);

  const sidebarExpandedWidth = '240px';
  const sidebarCollapsedWidth = '60px';
  const sidebarWidth = collapsed ? sidebarCollapsedWidth : sidebarExpandedWidth;

  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);

  const navigate = useNavigate();

  const fetchDeviceAttributes = useCallback(async (deviceName) => {
    const keys =
      'warranty_date,location,app_version,device_type,patch_v,group_id,history,modbus_config_name,patch_name,config_status,patch_status,modbus_v';

    try {
      const resp = await axiosInstance.get(
        apiUrls.factorytoolgroupAttributes(deviceName, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      if (attrMap.group_id) {
        localStorage.setItem('groupID', attrMap.group_id);
      }

      return {
        warranty: attrMap.warranty_date ?? null,
        location: attrMap.location ?? '',
        app_version: attrMap.app_version ?? '-',
        device_type: attrMap.device_type ?? 0,
        patch_v: attrMap.patch_v ?? '-',
        group_id: attrMap.group_id ?? null,
        history: attrMap.history ?? [],
        modbus_config_name: attrMap.modbus_config_name ?? '-',
        patch_name: attrMap.patch_name ?? '-',
        config_status: attrMap.config_status ?? '-',
        patch_status: attrMap.patch_status ?? '-',
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('Error fetching attributes:', error);
      return null;
    }
  }, []);
  // ✅ FETCH ALL DEVICES (with gap-filling loop)
  const backendPageMapRef = React.useRef({});
  const lastSearchRef = React.useRef('');

  const fetchAllDevices = useCallback(
    async (search = '', page = 0, pageSize = 10) => {
      setLoadingDevices(true);

      if (lastSearchRef.current !== search) {
        backendPageMapRef.current = {};
        lastSearchRef.current = search;
      }

      try {
        const pageNumber = typeof page === 'number' ? page : 0;

        let validDevices = [];
        let backendPage =
          backendPageMapRef.current[pageNumber] ??
          (pageNumber === 0 ? 0 : backendPageMapRef.current[pageNumber - 1] || 0);
        let hasNextPage = false;
        let totalPagesCount = 0;

        while (validDevices.length < pageSize) {
          const apiUrl = apiUrls.allDevices(search, backendPage);
          let resp;
          try {
            resp = await axiosInstance.get(apiUrl);
          } catch (apiError) {
            break;
          }

          const devices = resp.data.data || [];
          hasNextPage = resp.data.hasNext ?? false;
          totalPagesCount = resp.data.totalPages ?? 0;

          if (backendPageMapRef.current[pageNumber] === undefined) {
            backendPageMapRef.current[pageNumber] = backendPage;
          }

          if (devices.length === 0) break;

          const attrResults = await Promise.all(
            devices.map(async (d) => {
              const attrs = await fetchDeviceAttributes(d.name);
              return attrs ? d : null;
            }),
          );

          const validThisPage = attrResults.filter(Boolean);
          validDevices = validDevices.concat(validThisPage);

          if (!hasNextPage || validDevices.length >= pageSize) break;
          backendPage += 1;
        }

        backendPageMapRef.current[pageNumber + 1] = backendPage + 1;

        const finalDevices = validDevices.slice(0, pageSize);
        const sortedNames = sortDevices(finalDevices.map((d) => d.name));
        const sortedDevices = sortedNames.map((name) =>
          finalDevices.find((d) => d.name === name),
        );

        setDevices(sortedDevices);
        setCurrentPage(pageNumber);
        setTotalPages(totalPagesCount);
        setHasNext(hasNextPage);
        setSearchValue(search);
      } catch (error) {
        console.error('Error fetching devices:', error);
      } finally {
        setLoadingDevices(false);
      }
    },
    [fetchDeviceAttributes],
  );

  // After your fetchDeviceAttributes definition and state declarations

  // useEffect(() => {
  //   if (devices && devices.length > 0) {
  //     const sortedNames = sortDevices(devices.map((d) => d.name));
  //     sortedNames.forEach((name) => {
  //       fetchDeviceAttributes(name).then((data) => {
  //         console.log('Attributes for', name, data);
  //       });
  //     });
  //   }
  // }, [devices, fetchDeviceAttributes]);

  // ✅ WRAPPER FUNCTION FOR COMPLEX TABLE
  const handleFetchAllDevices = useCallback(
    (searchValue, loadingFlag, page) => {
      let pageNum = 0;
      if (typeof page === 'number') {
        pageNum = page;
      } else if (typeof loadingFlag === 'number') {
        pageNum = loadingFlag;
      } else {
        pageNum = 0;
      }

      console.log('Wrapper called - Page:', pageNum, 'Search:', searchValue);
      fetchAllDevices(searchValue || '', pageNum);
    },
    [fetchAllDevices],
  );

  // ✅ EXTRACT VALUE
  const extractValue = (field) => {
    if (!field || !Array.isArray(field) || field.length === 0) return null;
    const val = Number(field[0]?.value);
    return isNaN(val) ? null : val;
  };

  // ✅ METRIC CELL
  const MetricCell = (info) => {
    let value = info.getValue();
    if (value === '0') value = 0;

    let color = 'gray.400';

    if (value === 0) {
      color = 'green.500';
      return (
        <Text fontWeight="bold" color={color}>
          0.0
        </Text>
      );
    }

    if (typeof value === 'number' && !isNaN(value)) {
      if (value > 75) color = 'red.500';
      else if (value >= 50) color = 'orange.500';
      else color = 'green.500';

      return (
        <Text fontWeight="bold" color={color}>
          {value.toFixed(1)}
        </Text>
      );
    }

    return <Text color={color}>-</Text>;
  };

  const getUsedPercent = (used, metric, device_type) => {
    if (!configLoaded) return 0;

    const totals = totalsConfig[String(device_type)] || totalsConfig['2'];

    if (!totals) return null;
    if (used == null) return 0;

    const total = totals[metric];

    if (!total || total <= 0) return null;
    if (used < 0) return 0;

    return (used / total) * 100;
  };
  const fetchDeviceHealth = useCallback(() => {
    // 🚫 Prevent execution until config is loaded
    if (!devices || devices.length === 0 || !configLoaded) {
      setTableData([]);
      return;
    }

    setLoadingDevices(true);

    const requests = devices.map((d) =>
      axios.get(apiUrls.fetchDeviceHealth(d.name)),
    );

    Promise.allSettled(requests).then(async (results) => {
      const attributesResults = await Promise.all(
        devices.map((device) => fetchDeviceAttributes(device.name)),
      );

      const mappedData = devices.map((device, idx) => {
        const result = results[idx];
        const attrs = attributesResults[idx];

        const device_type =
          attrs && attrs.device_type !== undefined && attrs.device_type !== null
            ? attrs.device_type
            : 2; // default fallback

        // console.log('devicetypee----', device.name, device_type);

        // ✅ Get totals from runtime config
        const totals = totalsConfig[String(device_type)] || totalsConfig['2'];
        // console.log('Totals for device_type', device_type, totals);

        if (!totals) {
          console.warn('Totals config missing for device_type:', device_type);
        }

        if (result.status === 'fulfilled') {
          const item = result.value.data;

          const ramAvailable = extractValue(item.ram);
          const diskAvailable = extractValue(item.disk);
          const cpuValue = extractValue(item.cpu);
          const backupAvailable = extractValue(item.backup_used);

          // ✅ Calculate USED values
          const ramUsed =
            ramAvailable != null && totals?.ram != null
              ? totals.ram - ramAvailable
              : null;

          const diskUsed =
            diskAvailable != null && totals?.disk != null
              ? totals.disk - diskAvailable
              : null;

          const backupUsed =
            backupAvailable != null && totals?.backup != null
              ? totals.backup - backupAvailable
              : null;

          return {
            deviceName: item.device_id || device.name,
            device_type,

            cpu: getUsedPercent(cpuValue, 'cpu', device_type),
            ram: getUsedPercent(ramUsed, 'ram', device_type),
            disk: getUsedPercent(diskUsed, 'disk', device_type),
            backup: getUsedPercent(backupUsed, 'backup', device_type),
          };
        } else {
          return {
            deviceName: device.name,
            device_type,
            cpu: '-',
            ram: '-',
            disk: '-',
            backup: '-',
          };
        }
      });

      setTableData(mappedData);
      setLoadingDevices(false);
    });
  }, [devices, configLoaded, totalsConfig, fetchDeviceAttributes]);

  // ✅ TABLE COLUMNS
  const columnsData = [
    // { header: 'Sno', accessorKey: 'sno', cell: (info) => info.row.index + 1 },
    { header: 'Device Name', accessorKey: 'deviceName' },
    // {
    //   header: 'Device Type',
    //   accessorKey: 'device_type',
    //   cell: (info) => {
    //     const type = info.row.original.device_type;
    //     if (type === 1) return <Text>MINI</Text>;
    //     if (type === 2) return <Text>GRIDX</Text>;
    //     if (type === 3) return <Text>MINI SECURE</Text>;
    //     return <Text>-</Text>;
    //   },
    // },

    {
      header: 'Device Type',
      accessorKey: 'device_type',
      cell: (info) => {
        const type = info.getValue();
        if (type === 1) return <Text>MINI</Text>;
        if (type === 2) return <Text>GRIDX</Text>;
        if (type === 3) return <Text>MINI SECURE</Text>;
        return <Text>-</Text>;
      },
    },

    { header: 'CPU (%)', accessorKey: 'cpu', cell: MetricCell },
    { header: 'RAM (%)', accessorKey: 'ram', cell: MetricCell },
    { header: 'DISK (%)', accessorKey: 'disk', cell: MetricCell },
    { header: 'BACKUP (%)', accessorKey: 'backup', cell: MetricCell },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: (info) => {
        const row = info.row.original;
        return (
          <Button
            size="sm"
            colorScheme="green"
            variant="outline"
            onClick={() => {
              if (inEditDevice && onViewHealthDetails) {
                onViewHealthDetails(row.deviceName);
              } else {
                navigate(`/health-details/${row.deviceName}`);
              }
            }}
          >
            View Details
          </Button>
        );
      },
    },
  ];

  // ✅ FILTERS
  const filteredTableData = React.useMemo(() => {
    if (activeFilter === 'all') return tableData;

    return tableData.filter((d) => {
      const val = d[activeFilter];
      return val !== '-' && Number(val) > 75;
    });
  }, [tableData, activeFilter]);

  // ✅ COUNTS
  const highCpuCount = tableData.filter((d) => d.cpu > 75).length;
  const highRamCount = tableData.filter((d) => d.ram > 75).length;
  const highDiskCount = tableData.filter((d) => d.disk > 75).length;
  const highBackupCount = tableData.filter((d) => d.backup > 75).length;

  useEffect(() => {
    fetch(`/devicetotals.json?ts=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        setTotalsConfig(data);
        setConfigLoaded(true);
        // console.log('Config loaded:', data);
      })
      .catch((err) => {
        console.error('Failed to load config:', err);
        setConfigLoaded(true);
      });
  }, []);
  useEffect(() => {
    const count = parseInt(localStorage.getItem('totalAllDevices'), 10);
    if (!isNaN(count)) setTotalDeviceCount(count);
  }, []);
  useEffect(() => {
    fetchAllDevices('', 0);
  }, [fetchAllDevices]);
  useEffect(() => {
    if (devices.length > 0 && configLoaded) {
      fetchDeviceHealth();
      const interval = setInterval(fetchDeviceHealth, 60000);
      return () => clearInterval(interval);
    }
  }, [devices, configLoaded, fetchDeviceHealth]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  const handlePageChange = (page) => {
    const pageNum = typeof page === 'number' ? page : 0;
    fetchAllDevices(searchValue, pageNum);
  };

  const handleTableSearch = (value) => {
    fetchAllDevices(value || '', 0);
  };

  return (
    <Box ml={inEditDevice ? 0 : sidebarWidth} p={inEditDevice ? 1 : 6}>
      <BreadCrumb items={breadcrumbItems} />

      {!inEditDevice && (
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      )}

      <Heading size="lg" mb={6}>
        Device Health
      </Heading>

      {/* STATS */}
      <SimpleGrid columns={{ base: 1, md: 5 }} gap="20px" mb={6}>
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdDevices} color="blue.400" w="32px" h="32px" />}
            />
          }
          name="All Devices"
          value={totalDeviceCount || tableData.length}
          onClick={() => setActiveFilter('all')}
          cursor="pointer"
          border={activeFilter === 'all' ? '2px solid' : 'none'}
          borderColor="blue.400"
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdMemory} color="orange.400" w="32px" h="32px" />}
            />
          }
          name="CPU > 75%"
          value={highCpuCount}
          onClick={() => setActiveFilter('cpu')}
          cursor="pointer"
          border={activeFilter === 'cpu' ? '2px solid' : 'none'}
          borderColor="orange.400"
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdStorage} color="green.400" w="32px" h="32px" />}
            />
          }
          name="RAM > 75%"
          value={highRamCount}
          onClick={() => setActiveFilter('ram')}
          cursor="pointer"
          border={activeFilter === 'ram' ? '2px solid' : 'none'}
          borderColor="green.400"
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdSdStorage} color="red.400" w="32px" h="32px" />}
            />
          }
          name="Disk > 75%"
          value={highDiskCount}
          onClick={() => setActiveFilter('disk')}
          cursor="pointer"
          border={activeFilter === 'disk' ? '2px solid' : 'none'}
          borderColor="red.400"
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdBackup} color="purple.400" w="32px" h="32px" />}
            />
          }
          name="Backup > 75%"
          value={highBackupCount}
          onClick={() => setActiveFilter('backup')}
          cursor="pointer"
          border={activeFilter === 'backup' ? '2px solid' : 'none'}
          borderColor="purple.400"
        />
      </SimpleGrid>

      {/* TABLE */}
      <Box mt={6}>
        <ComplexTable
          title="Device Health"
          columnsData={columnsData}
          tableData={filteredTableData}
          loading={loadingDevices}
          enableLocalPagination={false}
          currentPage={currentPage}
          totalPages={totalPages}
          hasNext={hasNext}
          fetchAllDevices={handleFetchAllDevices}
          onPageChange={handlePageChange}
          onSearch={handleTableSearch}
          searchValue={searchValue}
        />
      </Box>

      <Flex
        as="footer"
        position="fixed"
        left={0}
        bottom="0"
        width="100%"
        bg={useColorModeValue('gray.100', 'navy.800')}
        color={useColorModeValue('gray.600', 'gray.200')}
        py={2}
        px={4}
        align="center"
        justify="space-between"
        zIndex={20}
        borderTop="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
      >
        <Text fontSize="sm" fontWeight="medium">
          Version 1.0.0
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          UGX Systems
        </Text>
      </Flex>
    </Box>
  );
};

export default DeviceHealth;
